import classNames from "classnames";
import { DateTime } from "luxon";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useState } from "react";
import DatePicker, { ReactDatePickerProps } from "react-datepicker";
import { Control, Controller, ControllerRenderProps, FieldValues, UseFormSetValue } from "react-hook-form";
import { FormGroup } from "./FormGroup";
import { serializeDate } from "./FormInput";
import { FormatDateInputSerializationFormat } from "./FormInput/types";

interface IFormDateRangeProps {
	startFieldName: string;
	endFieldName: string;
	control: Control;
	label: string;
	/** Number of max days for range length */
	maxRangeLimit?: number | undefined;
	/** Number of days for default range length */
	defaultRangeLength?: number | undefined;
	/** Disable date selection for dates in the future */
	disableFutureDates?: boolean | undefined;
	/** Disable date selection for dates outside defined max range */
	disableDatesOutOfValidRange?: boolean | undefined;
	serializationFormat?: FormatDateInputSerializationFormat;
	controlId?: string | undefined;
	datePickerProps?: ReactDatePickerProps;
	setValue: UseFormSetValue<FieldValues>;
	formResetFlag: boolean;
	defaultStartValue?: string;
	defaultEndValue?: string;
	disable?: boolean;
}

export const isDifferenceGreaterThanMaxRangeInDays = (start: Date, end: Date, maxRangeInDays: number): boolean => {
	if (!start || !end) return false;
	const differenceInMilliseconds = end.getTime() - start.getTime();
	const differenceInDays = differenceInMilliseconds / (24 * 60 * 60 * 1000);

	return differenceInDays > maxRangeInDays;
};

export const FormDateRangePicker = observer((props: IFormDateRangeProps) => {
	const { defaultRangeLength, disableFutureDates, disableDatesOutOfValidRange, maxRangeLimit, startFieldName, endFieldName, control, serializationFormat, label, controlId, datePickerProps, setValue, formResetFlag, defaultStartValue, defaultEndValue, disable } = props;

	const [startDate, setStartDate] = useState<Date | undefined>(defaultStartValue ? DateTime.fromISO(defaultStartValue).toJSDate() : undefined);
	const [endDate, setEndDate] = useState<Date | undefined>(defaultEndValue ? DateTime.fromISO(defaultEndValue).toJSDate() : undefined);
	const [endMaxDate, setEndMaxDate] = useState<Date | null>();

	const resolvedControlId = useMemo(() => (controlId ? controlId : `form-${label.replaceAll(/[\W_]/gi, "").toLowerCase()}`), [label, controlId]);
	const helpId = useMemo(() => `${resolvedControlId}-help`, [resolvedControlId]);
	const errorId = useMemo(() => `${resolvedControlId}-error`, [resolvedControlId]);

	useEffect(() => {
		if (formResetFlag) {
			let currentDate = new Date();
			currentDate.setDate(currentDate.getDate() - (defaultRangeLength ?? 0));
			setStartDate(currentDate);
			setEndDate(new Date());
		}
	}, [defaultRangeLength, formResetFlag]);

	useEffect(() => {
		if (!startDate) {
			let currentDate = new Date();
			currentDate.setDate(currentDate.getDate() - (defaultRangeLength ?? 0));
			setStartDate(currentDate);
		}

		if (!endDate) setEndDate(new Date());

		if (startDate! > endDate!) {
			setEndDate(new Date(startDate!));
		}

		let adaptRange: number | undefined = maxRangeLimit;
		if (maxRangeLimit != undefined && maxRangeLimit !== defaultRangeLength) adaptRange = maxRangeLimit - 1;
		let addMaxRange = disableDatesOutOfValidRange && adaptRange != undefined ? adaptRange : 0;

		if (!disableFutureDates && !disableDatesOutOfValidRange) {
			setEndMaxDate(null);
		} else {
			let maxDate: Date | null = null;

			if (disableDatesOutOfValidRange) {
				maxDate = new Date(startDate!);
				maxDate.setDate(maxDate.getDate() + addMaxRange);
			}

			if (disableFutureDates) {
				let currentDate = new Date();
				if (!maxDate || maxDate > currentDate) maxDate = currentDate;
			}

			setEndMaxDate(maxDate);
		}

		if (disableDatesOutOfValidRange && isDifferenceGreaterThanMaxRangeInDays(startDate!, endDate!, addMaxRange ?? Infinity)) {
			let maxDate: Date = new Date(startDate!);
			maxDate.setDate(startDate!.getDate() + addMaxRange);
			setEndDate(maxDate);
			setValue(endFieldName, serializeDate(maxDate, serializationFormat));
		}

		setValue(startFieldName, serializeDate(startDate!, serializationFormat));
		setValue(endFieldName, serializeDate(endDate!, serializationFormat));
	}, [startDate, endDate, disableDatesOutOfValidRange, defaultRangeLength, maxRangeLimit, setValue, startFieldName, serializationFormat, endFieldName, disableFutureDates]);

	const handleStartDateOnChange = (jsDate: Date | null, field: ControllerRenderProps) => {
		if (!jsDate) {
			let currentDate = new Date();
			currentDate.setDate(currentDate.getDate() - (defaultRangeLength ?? 0));
			jsDate = currentDate;
		}
		setStartDate(jsDate);
		field.onChange(serializeDate(jsDate, serializationFormat));
	};

	const handleEndDateOnChange = (jsDate: Date | null, field: ControllerRenderProps) => {
		jsDate = jsDate ?? new Date();
		setEndDate(jsDate);
		if (disableDatesOutOfValidRange && endDate) {
			let minDate = endDate;
			minDate.setDate(minDate.getDate() - (maxRangeLimit ?? 0));
		}
		field.onChange(serializeDate(jsDate, serializationFormat));
	};

	return (
		<>
			<label className="mb-n3">{label}</label>
			<Controller
				name={startFieldName}
				control={control}
				render={({ field, fieldState }) => (
					<FormGroup
						controlId={resolvedControlId}
						className={`form-daterange-picker mb-n3`}
						controlLabel=""
						helpId={helpId}
						errorId={errorId}
						errorMessage={fieldState.error?.message}
					>
						<label
							htmlFor="drpStartDate"
							className="form-sublabel"
						>
							Start Date
						</label>
						<DatePicker
							id="drpStartDate"
							{...datePickerProps}
							onChange={date => handleStartDateOnChange(date, field)}
							selected={startDate}
							selectsStart
							disabled={disable}
							startDate={startDate}
							endDate={endDate}
							className={classNames("form-control")}
							maxDate={disableFutureDates ? new Date() : null}
						/>
					</FormGroup>
				)}
			/>
			<Controller
				name={endFieldName}
				control={control}
				render={({ field, fieldState }) => {
					return (
						<FormGroup
							controlId={resolvedControlId}
							className={`form-daterange-picker`}
							controlLabel=""
							helpId={helpId}
							errorId={errorId}
							errorMessage={fieldState.error?.message}
						>
							<label
								htmlFor="drpEndDate"
								className="form-sublabel"
							>
								End Date
							</label>
							<DatePicker
								id="drpEndDate"
								onChange={date => handleEndDateOnChange(date, field)}
								selected={endDate}
								selectsEnd
								disabled={disable}
								startDate={startDate}
								endDate={endDate}
								className={classNames("form-control")}
								minDate={startDate}
								maxDate={endMaxDate}
							/>
						</FormGroup>
					);
				}}
			/>
		</>
	);
});
